/**
 * catalogo.tsx — el catálogo de la empresa activa, traído una vez.
 *
 * Vive en un contexto y no en cada pantalla porque lo necesitan tres: el
 * formulario de asiento, el diario y la pantalla de catálogo. Pedirlo en cada
 * una haría tres llamadas para el mismo dato y —peor— abriría la puerta a que
 * dos pantallas muestren catálogos distintos después de que alguien renombre
 * una cuenta.
 *
 * ── Se descarta al cambiar de empresa ─────────────────────────────────────
 *
 * El efecto depende de la empresa activa. Sin eso, cambiar de empresa dejaría
 * en pantalla el catálogo de la anterior: cuentas que no existen acá,
 * mostradas con toda naturalidad. Es la clase de error que no da ningún aviso.
 */
import {
  createContext, useContext, useEffect, useMemo, useState,
  type ReactNode,
} from 'react';
import { useSesion } from './sesion.tsx';
import { dimensionesQueExige } from './clasificacion.ts';
import {
  traerCatalogoCompleto, traerContrapartes,
  type CondicionDeClasificacion, type Contraparte, type CuentaReal,
  type VocabularioAnalitico,
} from '../datos/libros.ts';

interface Contexto {
  readonly cuentas: readonly CuentaReal[];
  /**
   * Qué valores admite cada dimensión CERRADA.
   *
   * Viene con el catálogo porque es la otra mitad de la misma pregunta: la
   * cuenta dice qué dimensión exige y esto dice qué se puede poner. Una
   * dimensión que no aparece está abierta —`customer`, `project`— y se pide con
   * un campo libre.
   */
  readonly vocabulario: VocabularioAnalitico;
  /**
   * Las reglas «esta dimensión exige esta otra».
   *
   * Viene con lo mismo y por lo mismo: la cuenta dice qué exige ELLA, y esto
   * dice qué exige lo que la línea ya escribió. Ver `useDimensionesQueExige`.
   */
  readonly condicional: readonly CondicionDeClasificacion[];
  /**
   * Las contrapartes de la empresa, con las dos banderas que separan clientes y
   * proveedores (0084).
   *
   * ▶ Vive en el catálogo porque es la otra mitad de la pregunta que ya vivía
   * aquí: la cuenta dice qué dimensión exige y el vocabulario qué se puede
   * poner; `supplier` y `customer` quedaron ABIERTOS en el vocabulario a
   * propósito —cerrarlos obligaría a sembrarlos por catálogo cuando son por
   * empresa—, pero NO son texto libre: la base los rechaza si no están en esta
   * lista. El `CampoDeDimension` las lee para ofrecer un desplegable en vez de
   * un `input` que termina en «esa contraparte no existe».
   */
  readonly contrapartes: readonly Contraparte[];
  /** `null` mientras no se ha intentado; `true` mientras se trae. */
  readonly cargando: boolean;
  readonly fallo: boolean;
  /** Vuelve a pedirlo. Se llama después de tocar el catálogo. */
  readonly recargar: () => void;
}

const Ctx = createContext<Contexto | null>(null);

export function ProveedorDeCatalogo({ children }: { children: ReactNode }) {
  const { pedir, sesion } = useSesion();
  const empresa = sesion?.empresa.id ?? null;

  const [cuentas, setCuentas] = useState<readonly CuentaReal[]>([]);
  const [vocabulario, setVocabulario] = useState<VocabularioAnalitico>({});
  const [condicional, setCondicional] =
    useState<readonly CondicionDeClasificacion[]>([]);
  const [contrapartes, setContrapartes] = useState<readonly Contraparte[]>([]);
  const [cargando, setCargando] = useState(false);
  const [fallo, setFallo] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (!empresa) {
      setCuentas([]); setVocabulario({}); setCondicional([]); setContrapartes([]);
      return;
    }
    let vivo = true;
    setCargando(true);
    setFallo(false);

    (async () => {
      try {
        // ▶ Las dos llamadas en paralelo: el catálogo y las contrapartes son
        // dos preguntas distintas, y pedirlas en serie dejaría el formulario
        // de asientos esperando una lista que la otra consulta ya terminó.
        const [c, cp] = await Promise.all([
          traerCatalogoCompleto(pedir),
          traerContrapartes(pedir),
        ]);
        if (vivo) {
          setCuentas(c.cuentas);
          setVocabulario(c.vocabulario);
          setCondicional(c.condicional);
          setContrapartes(cp);
        }
      } catch {
        // Lista vacía Y bandera de fallo. Solo la lista vacía haría que el
        // selector de cuentas se viera igual que en una empresa recién creada,
        // y no es lo mismo: una no tiene cuentas y la otra no se pudieron
        // traer. Las dos se ven idénticas si no se distinguen.
        if (vivo) {
          setCuentas([]); setVocabulario({}); setCondicional([]); setFallo(true);
        }
      } finally {
        if (vivo) setCargando(false);
      }
    })();

    return () => { vivo = false; };
  }, [pedir, empresa, tick]);

  const valor = useMemo<Contexto>(() => ({
    cuentas,
    vocabulario,
    condicional,
    cargando,
    fallo,
    contrapartes,
    recargar: () => setTick((n) => n + 1),
  }), [cuentas, vocabulario, condicional, cargando, fallo]);

  return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
}

export function useCatalogo(): Contexto {
  const c = useContext(Ctx);
  if (!c) throw new Error('useCatalogo fuera del ProveedorDeCatalogo');
  return c;
}

/**
 * Las cuentas que pueden recibir un movimiento.
 *
 * Hoja Y activa. Ofrecer una cuenta de mayor deja que alguien la elija y se
 * entere al guardar; ofrecer una desactivada convierte el desactivar en una
 * sugerencia. Las dos las rechaza la base — esto solo evita llegar hasta ahí.
 */
export function useCuentasDeMovimiento(): readonly CuentaReal[] {
  const { cuentas } = useCatalogo();
  return useMemo(
    () => cuentas.filter((c) => c.permiteMovimiento && c.activa),
    [cuentas],
  );
}

/** Cómo se llama una cuenta en el idioma en que se está mirando. */
export function nombreDe(c: CuentaReal, idioma: 'es' | 'en'): string {
  return idioma === 'en' ? c.nombreEn : c.nombre;
}

/** El texto de un fallo de la API, siempre desde el diccionario. */
export function claveDeFallo(codigo: string | null, clase: string): string {
  if (clase === 'conexion') return 'sb.conexion';
  return codigo ? `sb.${codigo}` : 'sb.unknown';
}

export const useRecargarCatalogo = () => useCatalogo().recargar;

/**
 * Qué dimensiones tiene que traer una línea, con el catálogo de esta empresa.
 *
 * La regla vive en `clasificacion.ts` —sin React, para que se pueda probar sin
 * montar una pantalla—; esto solo le pone delante las condiciones que llegaron
 * con el catálogo.
 *
 * ▶ Devuelve una FUNCIÓN y no una lista porque depende de lo que se va
 * escribiendo: un hook que recibiera la línea habría que llamarlo dentro del
 * `map` de las líneas, que son una cantidad que cambia entre renders.
 */
export function useDimensionesQueExige(): (
  cuenta: CuentaReal | null | undefined,
  puestas: Readonly<Record<string, string>>,
) => readonly string[] {
  const { condicional } = useCatalogo();
  return useMemo(
    () => (cuenta, puestas) => dimensionesQueExige(cuenta, condicional, puestas),
    [condicional],
  );
}

/**
 * Los valores que admite una dimensión, o `null` si está abierta.
 *
 * ▶ `null` y lista vacía NO son lo mismo, y acá importa: `null` es «esta
 * dimensión es abierta, pida texto libre» y una lista vacía sería «cerrada y
 * sin ningún valor», o sea un campo que no se puede llenar. Como el
 * vocabulario llega vacío mientras el catálogo se trae, se devuelve `null`
 * también en ese caso: un campo libre es recuperable; un desplegable vacío no.
 */
export function useValoresDe(dimension: string): readonly string[] | null {
  const { vocabulario } = useCatalogo();
  const v = vocabulario[dimension];
  // ▶ `Array.isArray` y no `v.length > 0`, que una CADENA también cumple.
  //
  // Ésa fue la versión que dejó la pantalla en blanco: `text[]` llegaba como
  // «{cloud,ga,…}», pasaba la comprobación de longitud, y el `.map` de abajo
  // lanzaba dentro del render. React desmonta el árbol entero cuando eso pasa,
  // así que el síntoma no es un mensaje: es una página vacía.
  return Array.isArray(v) && v.length > 0 ? v : null;
}
